import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DisplayOptions, Filter } from '../../models/filter-models';
import { DisplayType } from "../../models/enums";
import { Rarity } from "../../models/enums";
import { FilterService } from '../../services/filter.service';
import { AutocompleteComponent } from '../../common/autocomplete/autocomplete.component';
import { FormsModule } from '@angular/forms';
import { BaseType } from '../../models/item-data-models';
import { ItemDataService } from '../../services/item-data.service';
import { FilterTitleComponent } from '../../common/filter-title/filter-title.component';
import { poe1 } from '../../../game';
import { CurrencyTierService } from '../../services/tiering/currency-tier.service';
import { BaseComponent } from '../../common/base.component';
import { ActiveRules, ActiveSpecialRules } from '../../services/filter-generator.service';
import { TooltipWrapperComponent } from "../../common/tooltip-wrapper/tooltip-wrapper.component";
import { EquipmentType } from '../../models/equipment-type.enum';
import { EquipmentTierPickerComponent } from '../../common/equipment-tier-picker/equipment-tier-picker.component';
import { NavigationService } from '../../services/navigation.service';
import { CurrencyGeneralFiltersComponent } from './currency-general-filters/currency-general-filters.component';
import { FavouriteCurrencyFiltersComponent } from './favourite-currency-filters/favourite-currency-filters.component';

@Component({
  selector: 'app-currency-filters',
  imports: [FormsModule, AutocompleteComponent, FilterTitleComponent, TooltipWrapperComponent, EquipmentTierPickerComponent,
    CurrencyGeneralFiltersComponent, FavouriteCurrencyFiltersComponent],
  templateUrl: './currency-filters.component.html',
  styleUrl: './currency-filters.component.scss'
})
export class CurrencyFiltersComponent extends BaseComponent implements OnChanges {
  @Input({ required: true }) filter!: Filter;
  @Input({ required: true }) activeRules!: ActiveRules;
  @Input({ required: true }) activeSpecialRules!: ActiveSpecialRules;

  cheapCurrencyWithImages: BaseType[];

  runeBaseTypes: BaseType[];

  Rarity = Rarity;
  DisplayType = DisplayType;
  EquipmentType = EquipmentType;

  constructor(
    private filterService: FilterService,
    private itemDataService: ItemDataService,
    private currencyTierService: CurrencyTierService,
    private navigationService: NavigationService
  ) {
    super();

    const itemData = this.itemDataService.getItemData();
    this.runeBaseTypes = poe1 ? [] : (() => {
      const runes = itemData.find(i => i.itemType === 'Regular Runes')!.baseTypes;
      return runes.sort((a, b) => {
        // Put 'Lesser' first, 'Greater' last, then alpha
        const getRank = (name: string) => name.startsWith('Lesser') ? 0 : (name.startsWith('Greater') ? 2 : 1);
        const rankA = getRank(a.name);
        const rankB = getRank(b.name);
        if (rankA !== rankB) return rankA - rankB;
        return a.name.localeCompare(b.name);
      });
    })();

    this.cheapCurrencyWithImages = itemDataService.getFlatItemData()
      .filter(i => itemDataService.getCheapCurrency().includes(i.baseType.name))
      .map(i => ({ name: i.baseType.name, image: i.baseType.image }));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filter'] && this.filter) {
      this.ensureStackSettingsDefaults();
    }
  }

  ensureStackSettingsDefaults() {
    if (this.filter.stackedCurrencyMaxPromoteSteps == null) {
      this.filter.stackedCurrencyMaxPromoteSteps = 2;
    }
    this.filter.stackedGeneralCurrencyNeverPromote ??= ['None'];
    if (this.filter.promoteForcedHiddenCurrency == null) {
      this.filter.promoteForcedHiddenCurrency = false;
    }
    if (this.filter.promoteForcedHiddenCurrencyOneTierStackSize == null) {
      this.filter.promoteForcedHiddenCurrencyOneTierStackSize = poe1 ? 10 : 5;
    }
    if (this.filter.promoteForcedHiddenCurrencyTwoTiersStackSize == null) {
      this.filter.promoteForcedHiddenCurrencyTwoTiersStackSize = poe1 ? 20 : 10;
    }
  }

  updateFilter() {
    this.filterService.updateFilter();
  }

  rebuildCurrencyTiers() {
    this.currencyTierService.combineCurrencyTierListData(this.filter);
    this.updateFilter();
  }

  toggleHideRegularRunes = () => { this.filter.hideRegularRunes = !this.filter.hideRegularRunes; this.updateFilter(); }
  toggleHideUncutGems = () => { this.filter.hideUncutGems = !this.filter.hideUncutGems; this.updateFilter(); }
  toggleHideCurrency = () => { this.filter.hideCurrency = !this.filter.hideCurrency; this.updateFilter(); }

  updateHideUncutSkillGems() {
    if (this.filter.hideUncutSkillGemsBelowLevel && this.filter.hideUncutSkillGemsBelowLevel < 1) {
      this.filter.hideUncutSkillGemsBelowLevel = 1;
    } else if (this.filter.hideUncutSkillGemsBelowLevel && this.filter.hideUncutSkillGemsBelowLevel > 20) {
      this.filter.hideUncutSkillGemsBelowLevel = 20;
    }
    this.updateFilter();
  }

  updateHideUncutSpiritGems() {
    if (this.filter.hideUncutSpiritGemsBelowLevel && this.filter.hideUncutSpiritGemsBelowLevel < 4) {
      this.filter.hideUncutSpiritGemsBelowLevel = 4;
    } else if (this.filter.hideUncutSpiritGemsBelowLevel && this.filter.hideUncutSpiritGemsBelowLevel > 20) {
      this.filter.hideUncutSpiritGemsBelowLevel = 20;
    }
    this.updateFilter();
  }

  updateHideUncutSupportGems() {
    if (this.filter.hideUncutSupportGemsBelowLevel && this.filter.hideUncutSupportGemsBelowLevel < 1) {
      this.filter.hideUncutSupportGemsBelowLevel = 1;
    } else if (this.filter.hideUncutSupportGemsBelowLevel && this.filter.hideUncutSupportGemsBelowLevel > 5) {
      this.filter.hideUncutSupportGemsBelowLevel = 5;
    }
    this.updateFilter();
  }

  toggleHideScrolls = () => {
    this.filter.hideScrolls = !this.filter.hideScrolls;
    if (poe1 && !this.filter.hideScrolls) {
      this.filter.hideScrollsLowerThan = null;
    }
    this.updateFilter();
  }

  toggleHidePortalScrolls = () => {
    this.filter.hidePortalScrolls = !this.filter.hidePortalScrolls;
    if (poe1 && !this.filter.hidePortalScrolls) {
      this.filter.hidePortalScrollsLowerThan = null;
    }
    this.updateFilter();
  }

  toggleHideGold = () => {
    this.filter.hideGold = !this.filter.hideGold;
    if (this.filter.hideGold && !this.filter.hideGoldLowerThan) {
      this.filter.hideGoldLowerThan = 50;
    }

    this.updateFilter();
  }

  showLargeGoldStacksMinStackSizeChanged() {
    if (this.filter.showLargeGoldStacks && !this.filter.showLargeGoldStacksMinStackSize) {
      this.toggleShowLargeGoldStacks();
    } else {
      this.updateFilter();
    }
  }

  showHugeGoldStacksMinStackSizeChanged() {
    if (this.filter.showHugeGoldStacks && !this.filter.showHugeGoldStacksMinStackSize) {
      this.toggleShowHugeGoldStacks();
    } else {
      this.updateFilter();
    }
  }

  goldLowerThanChanged() {
    if (this.filter.hideGold && !this.filter.hideGoldLowerThan) {
      this.toggleHideGold();
    } else {
      this.updateFilter();
    }
  }

  toggleShowLargeGoldStacks = () => { this.filter.showLargeGoldStacks = !this.filter.showLargeGoldStacks; this.updateFilter(); }
  toggleShowHugeGoldStacks = () => { this.filter.showHugeGoldStacks = !this.filter.showHugeGoldStacks; this.updateFilter(); }

  navigateToLevelingGold = () => this.navigationService.navigateToFilterTitle('gold-leveling');

  toggleHideVerisium = () => {
    this.filter.hideVerisium = !this.filter.hideVerisium;
    if (this.filter.hideVerisium && !this.filter.hideVerisiumLowerThan) {
      this.filter.hideVerisiumLowerThan = 50;
    }
    this.updateFilter();
  }

  showLargeVerisiumStacksMinStackSizeChanged() {
    if (this.filter.showLargeVerisiumStacks && !this.filter.showLargeVerisiumStacksMinStackSize) {
      this.toggleShowLargeVerisiumStacks();
    } else {
      this.updateFilter();
    }
  }

  showHugeVerisiumStacksMinStackSizeChanged() {
    if (this.filter.showHugeVerisiumStacks && !this.filter.showHugeVerisiumStacksMinStackSize) {
      this.toggleShowHugeVerisiumStacks();
    } else {
      this.updateFilter();
    }
  }

  verisiumLowerThanChanged() {
    if (this.filter.hideVerisium && !this.filter.hideVerisiumLowerThan) {
      this.toggleHideVerisium();
    } else {
      this.updateFilter();
    }
  }

  toggleShowLargeVerisiumStacks = () => { this.filter.showLargeVerisiumStacks = !this.filter.showLargeVerisiumStacks; this.updateFilter(); }
  toggleShowHugeVerisiumStacks = () => { this.filter.showHugeVerisiumStacks = !this.filter.showHugeVerisiumStacks; this.updateFilter(); }

  navigateToLevelingVerisium = () => this.navigationService.navigateToFilterTitle('verisium-leveling');

  toggleStackedGeneralCurrencyPromotion() {
    this.filter.stackedGeneralCurrencyPromotionEnabled = !this.filter.stackedGeneralCurrencyPromotionEnabled;
    this.updateFilter();
  }

  togglePromoteForcedHiddenCurrency() {
    this.filter.promoteForcedHiddenCurrency = !this.filter.promoteForcedHiddenCurrency;
    this.updateFilter();
  }

  updateForcedHiddenStackSizes() {
    let one = this.filter.promoteForcedHiddenCurrencyOneTierStackSize;
    let two = this.filter.promoteForcedHiddenCurrencyTwoTiersStackSize;
    if (one == null || Number.isNaN(one) || one < 2) one = 2;
    if (two == null || Number.isNaN(two) || two < 2) two = 2;
    one = Math.floor(one);
    two = Math.floor(two);
    if (two < one) two = one;
    this.filter.promoteForcedHiddenCurrencyOneTierStackSize = one;
    this.filter.promoteForcedHiddenCurrencyTwoTiersStackSize = two;
    this.updateFilter();
  }

  toggleHighlight(displayOptions: DisplayOptions) {
    displayOptions.active = !displayOptions.active;
  }
}
